import { Option, Value } from 'baseui/select';
import { useFormState } from 'components/elements/form';
import useCruiseColumn from 'components/elements/table/editable-columns/cruise-column';
import TableEditableComponent from 'components/elements/table/table';
import {
  ActionType,
  ColumnDataType,
  IColumn,
} from 'components/elements/table/type';
import useGetCruiseShips from 'hooks/use-get-cruise-ships';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';

import {
  CruisePurchaseFormType,
  CruisePurchaseItemFormType,
} from '../form-type';

interface Props {
  parentIndex?: number;
}

function useGetCruiseTableList(
  cruiseShips: Option[],
): IColumn<CruisePurchaseItemFormType>[] {
  const { t } = useTranslation();
  const cruiseColumn = useCruiseColumn();
  return [
    {
      header: t('common:cruise_line'),
      accessorKey: 'cruiseId',
      minSize: 180,
      maxSize: 200,
      dataType: ColumnDataType.select,
      required: true,
      selectProps: cruiseColumn,
    },
    {
      header: t('cruise:ship'),
      accessorKey: 'cruiseShipId',
      minSize: 180,
      maxSize: 200,
      dataType: ColumnDataType.select,
      required: true,
      options: cruiseShips,
    },
    {
      header: t('common:from'),
      accessorKey: 'startedAt',
      minSize: 150,
      dataType: ColumnDataType.date,
      required: true,
    },
    {
      header: t('common:to'),
      accessorKey: 'endedAt',
      maxSize: 150,
      dataType: ColumnDataType.date,
      required: true,
    },
    {
      header: t('common:departure'),
      accessorKey: 'startedAt',
      minSize: 100,
      maxSize: 150,
      dataType: ColumnDataType.time,
      required: true,
    },
    {
      header: t('common:arrival'),
      accessorKey: 'endedAt',
      minSize: 100,
      maxSize: 150,
      dataType: ColumnDataType.time,
      required: true,
    },
    {
      header: t('common:route'),
      accessorKey: 'route',
      minSize: 200,
      dataType: ColumnDataType.text,
      required: true,
    },
  ];
}

export default function CruiseTable(props: Props) {
  const { parentIndex = 0 } = props;

  const { editable } = useFormState();

  const { getValues, control, clearErrors } =
    useFormContext<CruisePurchaseFormType>();

  const name = 'cruisePurchases' as const;

  const { update } = useFieldArray({
    control,
    name,
    keyName: '__customId',
  });

  const { cruiseShips } = useGetCruiseShips(`${name}.${parentIndex}.cruiseId`);

  const columns = useGetCruiseTableList(cruiseShips);

  const dispatch = React.useCallback(
    (action) => {
      const type = action.type as ActionType;
      const columnId = action.columnId as keyof CruisePurchaseItemFormType;
      const currentRowIndex = action.rowIndex as number;
      const cruises = getValues(name);
      const cruise = cruises[currentRowIndex];
      const value = action.value;
      const extra = action.extra as Value;
      const otherVal: () => Partial<CruisePurchaseItemFormType> = () => {
        if (columnId === 'cruiseId') {
          const cruiseName = extra?.[0]?.extra.name;
          return {
            cruiseName,
          };
        }
        if (columnId === 'cruiseShipId') {
          return {
            cruiseShipName: extra?.[0]?.label as string,
          };
        }
        return {};
      };

      if (type === 'update_cell') {
        update(currentRowIndex, {
          ...cruise,
          [columnId]: value,
          ...otherVal(),
        });
        clearErrors(`${name}.${parentIndex}.${columnId}`);
      }
    },
    [clearErrors, getValues, parentIndex, update],
  );

  return (
    <div style={{ width: '100%' }}>
      <TableEditableComponent
        columns={columns}
        data={[getValues(name)[parentIndex]]}
        dispatch={dispatch}
        disabled={!editable}
        noAddRemoveRow
        uniqueRowKey="customId"
      />
    </div>
  );
}
